// @ts-strict-ignore
import { useState } from 'react'
import { DateRange } from 'react-day-picker'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { toast } from '@node-space/ui-components'
import { logSentryError } from '@node-space/utils'
import {
  customDateValueOptions,
  exportFormatRadioOptions,
} from 'components/export/onceOff/constants/exportOptions'
import { useSendTransactionsReportMutation } from 'hooks/mutations/useSendTransactionsReportMutation'
import { buildWalletExportPayload } from 'services/TransactionService'
import { BaseErrorResponse } from 'types/beneficiaries'
import { ErrorCodes } from 'types/errorCodes'
import {
  ExportFileFormat,
  ExportFileFormatType,
  ExportTabItems,
  OnceOffExportForm,
  OnceOffExportSteps,
  OnceOffExportStepsType,
  ReportTypes,
} from 'types/transactionsExport'
import {
  AmplitudeEvent,
  AmplitudeEventAction,
  AmplitudeEventCategory,
} from 'utils/amplitude/amplitudeEvents'
import track from 'utils/tracker'
import { ExportConfig, TransactionExportHookResult } from './types'

/**
 * Base hook for transaction exports
 * Provides all common functionality shared by different export types
 * Behavior is customized through the ExportConfig parameter
 *
 * @param config - Configuration object defining export behavior
 * @param handleClose - Callback to close the export modal
 * @returns Hook result with all state, form controls, and actions
 *
 * @example
 * // Rewards export
 * const exportValues = useOnceOffTransactionExportBase({
 *   walletIdResolver: () => ({ lsid: walletId }),
 *   features: { allowBalanceReport: false },
 *   errorContext: 'RewardsTransactionExport'
 * }, handleClose)
 *
 * @example
 * // Wallet export with legacy support
 * const exportValues = useTransactionExportBase({
 *   walletIdResolver: () => ({ id: wallet?.id, lsid: wallet?.lsid }),
 *   features: { allowBalanceReport: true },
 *   errorContext: 'WalletTransactionExport'
 * }, handleClose)
 */
export const useOnceOffTransactionExportBase = (
  config: ExportConfig,
  handleClose: () => void
): TransactionExportHookResult => {
  const { t } = useTranslation()

  const walletIds = config.walletIdResolver()

  const {
    mutate: sendTransactionsReport,
    isPending: isLoadingSendTransactionsReport,
    error: sendTransactionsReportError,
    isError: isErrorSendTransactionsReport,
  } = useSendTransactionsReportMutation()

  const [step, setStep] = useState<OnceOffExportStepsType>(OnceOffExportSteps.FORM)
  const [exportFormat, setExportFormat] = useState<ExportFileFormatType>(ExportFileFormat.CSV)
  const [dateValue, setDateValue] = useState<DateRange>({
    from: null,
    to: null,
  })

  const exportFormatOptions = exportFormatRadioOptions()
  const exportCustomDateOptions = customDateValueOptions(dateValue?.from, dateValue?.to)

  const { watch, setValue } = useForm<OnceOffExportForm>({
    defaultValues: {
      reportType: ReportTypes.TRANSACTION,
      selectedDateOptionName: exportCustomDateOptions?.last30Days?.name,
      selectedExportFormat: exportFormatOptions[0].value as ExportFileFormatType,
      balanceDate: undefined,
    },
    mode: 'onChange',
  })
  const formValues: OnceOffExportForm = watch()

  const customDateValueName = formValues.selectedDateOptionName
  const selectedFromDateVal = exportCustomDateOptions?.[customDateValueName]?.fromDate
  const selectedToDateVal = exportCustomDateOptions?.[customDateValueName]?.toDate
  const selectedDateRange = { from: selectedFromDateVal, to: selectedToDateVal }

  // Build amplitude properties with customization
  const baseAmplitudeProperties = {
    category: AmplitudeEventCategory.MERCHANT_PORTAL,
    action: AmplitudeEventAction.LOAD,
    type: ExportTabItems.ONCE_OFF,
    format: formValues?.selectedExportFormat,
    from: selectedFromDateVal,
    to: selectedToDateVal,
  }

  const amplitudeProperties = {
    ...baseAmplitudeProperties,
    ...config.amplitudePropsExtras?.(formValues, walletIds),
  }

  // Common success handler
  const createSuccessHandler = () => {
    track.Amp.track(AmplitudeEvent.EOF_CREATE_EXPORT_INITIATE_SUCCESS, {
      ...amplitudeProperties,
    })

    toast({
      header: t('exports.checkYourMail'),
      variant: 'success',
      body: t('exports.checkYourMailDescription'),
    })
    handleClose()
  }

  // Common error handler
  const createErrorHandler = (walletExport?: any) => (error: BaseErrorResponse) => {
    track.Amp.track(AmplitudeEvent.EOF_CREATE_EXPORT_INITIATE_ERROR, {
      ...amplitudeProperties,
      error,
    })

    if (error?.data?.code !== ErrorCodes.REPORT_LEDGER_DUPLICATE_REQUEST) {
      logSentryError(
        `Error from ${config.errorContext} - handleTransactionExportToEmail`,
        error,
        walletExport
      )
    }
  }

  // Build export payload with customization
  const buildExportPayload = () => {
    const basePayload = {
      reportType: formValues?.reportType,
      selectedExportFormat: formValues?.selectedExportFormat,
      selectedDateRange,
      walletLSID: walletIds.lsid,
    }

    const extras = config.buildPayloadExtras?.(formValues, walletIds) || {}

    return buildWalletExportPayload({
      ...basePayload,
      ...extras,
    })
  }

  // Reporting service handler
  const handleTransactionExportToEmail = () => {
    const walletExport = buildExportPayload()

    sendTransactionsReport(walletExport, {
      onSuccess: createSuccessHandler,
      onError: createErrorHandler(walletExport),
    })
  }

  return {
    step,
    setStep,
    formValues,
    setValue,
    exportCustomDateOptions,
    dateValue,
    setDateValue,
    exportFormatOptions,
    exportFormat,
    setExportFormat,
    handleTransactionExportToEmail,
    isExporting: isLoadingSendTransactionsReport,
    handleClose,
    sendTransactionsReportError,
    isErrorSendTransactionsReport,
  }
}
